/**
 * Reusable skeleton loader components for CLC theme.
 *
 * Usage:
 *   <Skeleton lines={3} />
 *   <SkeletonCard />
 *   <SkeletonTable rows={5} cols={4} />
 *   <SkeletonTestSeries />
 */

export function Skeleton({ lines = 3, className = "" }) {
  return (
    <div className={`animate-pulse space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className={`h-4 bg-gray-200 rounded ${
            i === lines - 1 ? "w-3/4" : "w-full"
          }`}
        />
      ))}
    </div>
  );
}

export function SkeletonBlock({ className = "" }) {
  return (
    <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
  );
}

export function SkeletonCard() {
  return (
    <div className="animate-pulse border rounded-2xl p-6 shadow-sm">
      <div className="flex flex-col items-center space-y-4">
        <div className="h-6 bg-gray-200 rounded w-1/2" />
        <div className="h-4 bg-gray-200 rounded w-1/3" />
        <div className="h-12 bg-gray-200 rounded w-full" />
        <div className="space-y-2 w-full">
          <div className="h-4 bg-gray-200 rounded w-full" />
          <div className="h-4 bg-gray-200 rounded w-5/6" />
          <div className="h-4 bg-gray-200 rounded w-4/6" />
        </div>
        <div className="h-10 bg-gray-200 rounded w-full" />
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 4 }) {
  return (
    <div className="animate-pulse">
      {/* Header */}
      <div className="flex gap-4 mb-4 bg-gray-100 p-3 rounded">
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="h-4 bg-gray-300 rounded flex-1" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex gap-4 py-3 border-b">
          {Array.from({ length: cols }).map((_, col) => (
            <div key={col} className="h-4 bg-gray-200 rounded flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonTestSeries() {
  return (
    <div className="animate-pulse space-y-6">
      <div className="flex gap-3 justify-center">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-10 bg-gray-200 rounded-full w-24" />
        ))}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </div>
  );
}

export function SkeletonBookSection() {
  return (
    <div className="animate-pulse">
      <div className="flex gap-4 overflow-hidden">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex-shrink-0 w-72 border rounded-lg p-4">
            <div className="h-48 bg-gray-200 rounded mb-3" />
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
            <div className="h-4 bg-gray-200 rounded w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonCourseDetail() {
  return (
    <div className="animate-pulse">
      <div className="grid lg:grid-cols-9 gap-8 py-6">
        <div className="lg:col-span-5 space-y-4">
          <div className="h-12 bg-gray-200 rounded w-32" />
          <div className="h-10 bg-gray-200 rounded w-2/3" />
          <div className="space-y-3 mt-6">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-4/6" />
            <div className="h-4 bg-gray-200 rounded w-full" />
          </div>
        </div>
        <div className="lg:col-span-4 space-y-4">
          <div className="h-64 bg-gray-200 rounded-lg" />
          <div className="h-14 bg-gray-200 rounded-lg" />
          <div className="h-12 bg-gray-200 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonSwiper() {
  return (
    <div className="animate-pulse flex gap-5 overflow-hidden py-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex-shrink-0 w-60">
          <div className="h-36 bg-gray-200 rounded-lg mb-3" />
          <div className="h-5 bg-gray-200 rounded w-3/4" />
        </div>
      ))}
    </div>
  );
}

export default Skeleton;
